import React from 'react'
import moment from 'moment-timezone'
import { browserHistory } from 'react-router'

import { AddToCalendarButton, Button, Contact, Heading } from 'core'
import { absoluteLinkTo } from 'event-taxonomy'
import { formatTimeRange, formatWebinarDate } from 'helpers'
import { Section } from 'layout'

import CompleteButton from './CompleteButton'
import RegisterButton from './RegisterButton'

import css from './style.scss'
import Vimeo from '@u-wave/react-vimeo'
import Youtube from '@u-wave/react-youtube'

const extractVideoID = (url) => {
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/
  const match = url.match(regExp)
  if (match && match[7].length === 11) {
    return match[7]
  }
}

/**
 * The `<VideoHeader />` component generates a header
 * of the specified event object passed down to it.
 * It is exclusively used for our single Event pages.
 */

const VideoHeader = (props) => {
  const { webinar, isAuthenticated } = props
  const {
    title,
    video_url: url,
    registered,
    completed_at: completedAt,
    start_at: start,
    end_at: end,
    facilitator,
    status,
    is_external: isExternal,
    external_url: externalUrl,
    zoom_enabled: zoomEnabled,
    join_opens_at: joinOpensAt,
    zoom_join_url: zoomJoinUrl,
    zoom_general_join_url: zoomGeneralJoinUrl
  } = webinar
  const completed = !!completedAt
  const videoStates = {
    notStarted: true,
    started: false,
    finished: false,
    duration: 0
  }

  // Set the video states based on previous watch data
  if (status === 'COMPLETED') {
    videoStates.notStarted = false
    videoStates.started = true
    videoStates.finished = true
  } else if (status === 'STARTED') {
    videoStates.notStarted = true
    videoStates.started = true
  }

  // Used for YouTube calculations

  /* Safe-check in case facilitator user account gets deleted */
  if (facilitator) {
    facilitator.role = 'Facilitator'
  }

  let date, times

  if (start) {
    date = formatWebinarDate(start)
  }

  if (start && end) {
    times = formatTimeRange(start, end)
  }

  const now = moment()
  // External/Zoom events keep the register CTA live through the whole
  // event (there's a join link to reach), instead of swapping to
  // "complete" the moment the event starts like a plain webinar does.
  const liveOrExternal = zoomEnabled || isExternal
  const showRegister = liveOrExternal
    ? (end && moment(end).isAfter(now))
    : (start && moment(start).isAfter(now))
  const showComplete = liveOrExternal
    ? (end && moment(end).isSameOrBefore(now))
    : (start && moment(start).isSameOrBefore(now))
  // Once the webinar has started there's nothing left to put on a calendar.
  const showAddToCalendar = start && end && moment(start).isAfter(now)
  // The join/go-to-event window opens 15 minutes before start for Zoom
  // events; external-only (non-Zoom) events open at start itself.
  const windowOpensAt = zoomEnabled ? joinOpensAt : start
  const windowOpen = !!windowOpensAt && moment(windowOpensAt).isSameOrBefore(now)
  const joinUrl = zoomJoinUrl || zoomGeneralJoinUrl

  /**
   * Get the Required Video Player
   * @param provider
   * @returns {JSX.Element}
   */
  const genVideoContainer = (provider) => {
    switch (provider) {
      case 'vimeo':
        return (
          <div className={css.video}>
            <Vimeo
              playsInline
              video={url}
              autoplay={false}
              speed
              controls
              responsive
              onTimeUpdate={(e) => handleVideoTime(e)}
            />
          </div>
        )
      case 'youtube':
        return (
          <div className={css.video}>
            <Youtube
              playsInline
              video={extractVideoID(url)}
              autoplay={false}
              onReady={() => handleYoutubeVideoReady()}
              modestBranding
              showRelatedVideos={false}
              annotations={false}
              allowFullscreen
            />
          </div>
        )
      default:
        // External/Zoom events have no video_url until a recording is
        // posted after the fact — an iframe with no src renders broken.
        if (!url) return null
        return (
          <div className={css.video}>
            <iframe
              src={url}
              allowFullScreen
            />
          </div>
        )
    }
  }

  const videoContent = genVideoContainer(webinar.video_provider)

  /**
   * Handle YouTube Video Ready
   */
  const handleYoutubeVideoReady = () => {
    // Grab Iframe containing YouTube Player
    const iframeWindow = document.getElementsByTagName('iframe')[0].contentWindow

    // Watch messages from IFrame
    window.addEventListener('message', (e) => {
      // Gate unwanted events
      if (e.source !== iframeWindow) return

      const data = JSON.parse(e.data)

      // The Duration is only in the first message, grab it while we have the chance
      if (
        data.event === 'infoDelivery' &&
        data.info &&
        data.info.duration &&
        !videoStates.duration
      ) {
        videoStates.duration = data.info.duration
      }

      if (
        data.event === 'infoDelivery' &&
        data.info &&
        data.info.currentTime &&
        videoStates.duration
      ) {
        if (Math.floor(data.info.currentTime) > 10 && videoStates.started === false) {
          handleVideoStarted()
        } else if ((data.info.currentTime / videoStates.duration) > 0.89 && videoStates.finished === false) {
          handleVideoFinished()
        }
      }
    })
  }

  /**
   * Handle Vimeo Video Time
   * @param e
   */
  const handleVideoTime = (e) => {
    const { percent, seconds } = e

    if (seconds > 10 && videoStates.started === false) {
      handleVideoStarted()
    } else if (percent > 0.89 && videoStates.finished === false) {
      handleVideoFinished()
    }
  }

  /**
   * Handle Video Started
   */
  const handleVideoStarted = () => {
    // Set States
    videoStates.started = true
    videoStates.notStarted = false

    props.started()
  }

  /**
   * Handle Video Finished
   */
  const handleVideoFinished = () => {
    // Set States
    videoStates.finished = true

    props.complete()
  }

  const authenticatedButtonContent = (
    <span className={css.button}>
      {
        showRegister &&
          <RegisterButton
            register={props.register}
            unregister={props.unregister}
            isRegistered={registered}
            isLoading={props.isRegistering || props.isUnregistering}
            isExternal={isExternal}
            externalUrl={externalUrl}
            zoomEnabled={zoomEnabled}
            windowOpen={windowOpen}
            joinUrl={joinUrl}
          />
      }
      {
        showComplete &&
          <CompleteButton
            complete={props.complete}
            isCompleted={completed}
            isLoading={props.isCompleting}
          />
      }
    </span>
  )

  const publicButtonContent = (
    <span className={`${css.button} ${css.publicButton}`}>
      <Button
        size='primary'
        noMargin
        onClick={handleJoinButtonClicked}
      >
        Become a member
      </Button>
    </span>
  )

  const VideoSection = ({ video, title, cta, description, footer }) => {
    return (
      <div className={`${css.details} ${video ? '' : css.noVideo}`}>
        <div className={css.mobileVideoHeader}>
          <div>
            {title}
          </div>
          <div className={css.mobileCTA}>
            {cta}
          </div>
        </div>
        {video}
        <div className={css.rightDetailSection} tabIndex={0} aria-label='Webinar details'>
          <div>
            {title}
          </div>
          <div>
            {description}
          </div>
          <div>
            {cta}
          </div>
          <div>
            {footer}
          </div>
        </div>
        <div className={css.mobileVideoFooter}>
          {description && (
            <div className={css.mobileVideoFooterSection}>
              {description}
            </div>
          )}
          {footer && (
            <div className={css.mobileVideoFooterSection}>
              {footer}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={css.wrapper}>
      <Section>
        <Heading size='h2' freight weight='medium'>{title}</Heading>
      </Section>
      <Section mobileNoPadding>
        {
          isAuthenticated
            ? <VideoSection
                video={videoContent}
                title={(
                  <div>
                    {
                    date &&
                      <p className={css.date}>{date}</p>
                  }
                    {
                    times &&
                      <p className={css.time} size='h5'>{times}</p>
                  }
                    {
                    showAddToCalendar &&
                      <AddToCalendarButton
                        className={css.addToCalendar}
                        name={title}
                        description={`View details: ${absoluteLinkTo({ type: 'webinar', target: webinar })}`}
                        startAt={start}
                        endAt={end}
                      />
                  }
                  </div>)}
                cta={authenticatedButtonContent}
                description={facilitator && <Contact size='lg' user={facilitator} nameItalic />}
                footer={(
                  <div>
                    <p className={css.contactUs}>Contact us</p>
                    <p className={css.email}>webinar@ncfdd.org</p>
                  </div>
                )}
              />
            : <VideoSection
                video={videoContent}
                title={(
                  <Heading size='h4' weight='500' freight noMargin center>
                    Join the NCFDD today
                  </Heading>
                )}
                cta={publicButtonContent}
                description={(
                  <p>
                    The NCFDD serves members from almost every discipline and representing a broad range of colleges and
                    universities globally, join today and start transforming your personal academic life.
                  </p>
                )}
              />
        }
      </Section>
    </div>
  )
}

const handleJoinButtonClicked = () => browserHistory.push('/join')

VideoHeader.propTypes = {
  webinar: React.PropTypes.shape({
    id: React.PropTypes.number,
    type: React.PropTypes.string,
    title: React.PropTypes.string,
    start_at: React.PropTypes.string,
    end_at: React.PropTypes.string,
    video_embed_url: React.PropTypes.string,
    is_external: React.PropTypes.bool,
    external_url: React.PropTypes.string,
    zoom_enabled: React.PropTypes.bool,
    join_opens_at: React.PropTypes.string,
    zoom_join_url: React.PropTypes.string,
    zoom_general_join_url: React.PropTypes.string,
    facilitator: React.PropTypes.shape({
      name: React.PropTypes.string,
      avatar_url: React.PropTypes.string,
      title: React.PropTypes.string,
      role: React.PropTypes.string
    })
  }).isRequired,
  register: React.PropTypes.func.isRequired,
  unregister: React.PropTypes.func.isRequired,
  isRegistering: React.PropTypes.bool.isRequired,
  isUnregistering: React.PropTypes.bool.isRequired,
  complete: React.PropTypes.func.isRequired,
  started: React.PropTypes.func.isRequired,
  isCompleting: React.PropTypes.bool.isRequired,
  isAuthenticated: React.PropTypes.bool.isRequired
}

export default VideoHeader
